// "Approve firmware update" — the one place the NeOS firmware offer, the
// approval and its outcome are rendered (ADR 0202; core ADR-164). The
// firmware twin of BoatUpdateBanner: what to say is decided by
// `firmwareBannerView` (lib/firmwareBannerState.ts) from what the BOAT
// reported, never from the POST's 202. This file owns only the POST, the
// poll, the dismissal, and the markup.
//
// The owner decision the copy honors: consent covers download AND install —
// the gateway fetches nothing until this approval. The first button opens an
// inline confirmation naming the exact signed artifact's version and byte
// size; only the second button sends consent.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../api';
import { firmwareBannerView, formatFirmwareSize } from '../../lib/firmwareBannerState';
import {
  forgetFirmwareUpdateAttempt,
  readFirmwareUpdateAttempt,
  rememberFirmwareUpdateAttempt,
  type FirmwareUpdateAttempt,
} from '../../lib/firmwareUpdateAttempt';
import { dismissFirmwareVersion, dismissedFirmwareVersion } from '../../lib/updateBannerDismiss';
import type { Boat } from '../../lib/useBoats';

/** How often to re-read the boat while an approval is outstanding. */
const POLL_MS = 15_000;

function apiErrorMessage(err: unknown): string {
  const body = (err as { body?: { message?: unknown } } | undefined)?.body;
  if (body && typeof body.message === 'string' && body.message.length > 0) return body.message;
  return 'The approval could not be sent. Try again.';
}

export interface BoatFirmwareBannerProps {
  boat: Boat;
  /** Re-read GET /v1/boats — the banner polls this while it is waiting. */
  onRefresh: () => void;
}

export function BoatFirmwareBanner({ boat, onRefresh }: BoatFirmwareBannerProps) {
  const firmware = boat.firmware ?? null;
  const firmwareUpdate = firmware?.updateAvailable ?? null;
  const firmwareLatest = firmwareUpdate?.version ?? null;
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<FirmwareUpdateAttempt | null>(() =>
    readFirmwareUpdateAttempt(boat.id)
  );
  const [confirming, setConfirming] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());
  const [dismissedVersion, setDismissedVersion] = useState(() => dismissedFirmwareVersion(boat.id));
  const reviewButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);

  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  const view = firmwareBannerView({
    firmwareVersion: firmware?.version ?? null,
    firmwareUpdate,
    lastUpdate: firmware?.lastUpdate ?? null,
    attempt,
    sending,
    error,
    dismissedVersion,
    now,
  });

  const polling = view.waiting && !view.timedOut;
  useEffect(() => {
    if (!polling) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
      refresh.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [polling]);

  // Terminal boat truth, timeout, or a newer offer retires only the local
  // correlation record. The boat-reported status remains in GET /v1/boats.
  useEffect(() => {
    if (attempt === null) return;
    const superseded = firmwareUpdate !== null && firmwareUpdate.version !== attempt.targetVersion;
    if (view.timedOut) {
      // Stop carrying the correlation into a future mount, but retain it in
      // this render so the honest "No answer" status does not disappear as a
      // side effect of cleanup.
      forgetFirmwareUpdateAttempt(boat.id);
      return;
    }
    if (!view.settled && !superseded) return;
    forgetFirmwareUpdateAttempt(boat.id);
    setAttempt(null);
  }, [attempt, boat.id, firmwareUpdate, view.settled, view.timedOut]);

  // This is an inline confirm rather than a native prompt so the exact size,
  // reboot consequence and release notes remain visible and testable. Move
  // focus into it; Cancel returns focus to the review trigger.
  useEffect(() => {
    if (!confirming) return;
    const frame = window.requestAnimationFrame(() => cancelButton.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [confirming]);

  useEffect(() => {
    if (confirming && (view.action === null || view.action.disabled)) setConfirming(false);
  }, [confirming, view.action]);

  const approve = useCallback(async () => {
    if (firmwareUpdate === null) return;
    setConfirming(false);
    setSending(true);
    setError(null);
    try {
      const res = await api<{ status: string; idempotencyKey: string }>(
        `/v1/boats/${boat.id}/firmware/install`,
        { method: 'POST', body: { version: firmwareUpdate.version } }
      );
      const startedAt = Date.now();
      const nextAttempt: FirmwareUpdateAttempt = {
        commandKey: res.idempotencyKey,
        targetVersion: firmwareUpdate.version,
        startedAt,
      };
      rememberFirmwareUpdateAttempt(boat.id, nextAttempt);
      setAttempt(nextAttempt);
      setNow(startedAt);
      refresh.current();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  }, [boat.id, firmwareUpdate]);

  const cancelReview = useCallback(() => {
    setConfirming(false);
    window.requestAnimationFrame(() => reviewButton.current?.focus());
  }, []);

  const dismiss = useCallback(() => {
    if (firmwareLatest === null) return;
    dismissFirmwareVersion(boat.id, firmwareLatest);
    setDismissedVersion(firmwareLatest);
  }, [boat.id, firmwareLatest]);

  // A boat that is not firmware-managed renders nothing, ever.
  if (firmware === null || !view.visible) return null;

  return (
    <div
      className={`update-banner${view.status?.tone === 'failed' ? ' update-banner--failed' : ''}`}
      data-testid={`firmware-banner-${boat.id}`}
    >
      <div className="update-banner-text">
        {view.offer ? <span className="update-banner-head">{view.offer}</span> : null}
        {view.status ? (
          <span
            className="update-banner-status"
            role="status"
            aria-live="polite"
            data-testid={`firmware-status-${boat.id}`}
          >
            {view.status.text}
          </span>
        ) : null}
      </div>
      {view.action && !confirming ? (
        <button
          ref={reviewButton}
          type="button"
          className="update-banner-action"
          disabled={view.action.disabled}
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
          data-testid={`approve-firmware-${boat.id}`}
        >
          {view.action.label}
        </button>
      ) : null}
      {confirming && firmwareUpdate !== null && view.action && !view.action.disabled ? (
        <div
          className="update-banner-confirm"
          role="group"
          aria-labelledby={`firmware-confirm-title-${boat.id}`}
          aria-describedby={`firmware-confirm-detail-${boat.id}`}
          data-testid={`firmware-confirm-${boat.id}`}
        >
          <div className="update-banner-confirm-copy">
            <strong id={`firmware-confirm-title-${boat.id}`}>
              Install NeOS {firmwareUpdate.version}?
            </strong>
            <span id={`firmware-confirm-detail-${boat.id}`}>
              This downloads {formatFirmwareSize(firmwareUpdate.size)}, installs it to the gateway’s
              inactive system slot, and then restarts the gateway. Boat monitoring will be briefly
              unavailable. Includes Neomaris Core {firmwareUpdate.tomboloCoreVersion}.
            </span>
            {firmwareUpdate.notes ? (
              <a href={firmwareUpdate.notes} target="_blank" rel="noreferrer">
                Read release notes
              </a>
            ) : null}
          </div>
          <div className="update-banner-confirm-actions">
            <button
              ref={cancelButton}
              type="button"
              className="update-banner-confirm-cancel"
              onClick={cancelReview}
              data-testid={`cancel-firmware-${boat.id}`}
            >
              Cancel
            </button>
            <button
              type="button"
              className="update-banner-confirm-commit"
              onClick={() => void approve()}
              data-testid={`confirm-firmware-${boat.id}`}
            >
              Download and install
            </button>
          </div>
        </div>
      ) : null}
      {view.dismissible && !confirming ? (
        <button
          type="button"
          className="update-banner-dismiss"
          onClick={dismiss}
          aria-label={`Dismiss the NeOS ${firmwareLatest ?? ''} firmware update notice`}
          data-testid={`dismiss-firmware-${boat.id}`}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
